/**
 * General Tab - Name, slug, description, status
 */

import type { SkinStatus } from '../../../public/deals/components/slot-machine/themes/skin-types';

interface GeneralFormFields {
  name: string;
  slug: string;
  description: string;
  status: SkinStatus;
  isDefault: boolean;
}

interface GeneralTabProps extends GeneralFormFields {
  onUpdate: <K extends keyof GeneralFormFields>(field: K, value: GeneralFormFields[K]) => void;
}

export default function GeneralTab({
  name,
  slug,
  description,
  status,
  isDefault,
  onUpdate,
}: GeneralTabProps) {
  // Auto-generate slug from name
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleNameChange = (newName: string) => {
    onUpdate('name', newName);
    // Only auto-update slug if it's empty or matches the old auto-generated slug
    if (!slug || slug === generateSlug(name)) {
      onUpdate('slug', generateSlug(newName));
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h2 className="text-lg font-semibold mb-4">General Information</h2>
        <p className="text-sm text-gray-500 mb-6">
          Basic information about your slot machine skin.
        </p>
      </div>

      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Skin Name *
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder="e.g., Vegas Gold Casino"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Slug */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          URL Slug *
        </label>
        <div className="flex items-center">
          <span className="px-3 py-2 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg text-gray-500">
            /slots/
          </span>
          <input
            type="text"
            value={slug}
            onChange={(e) => onUpdate('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="vegas-gold-casino"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Only lowercase letters, numbers, and hyphens allowed
        </p>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => onUpdate('description', e.target.value)}
          placeholder="A brief description of this slot machine theme..."
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Status */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Status
        </label>
        <select
          value={status}
          onChange={(e) => onUpdate('status', e.target.value as SkinStatus)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Only published skins are visible to users
        </p>
      </div>

      {/* Default indicator */}
      {isDefault && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-amber-800 text-sm">
            <strong>This is the default skin.</strong> It will be used when no specific skin is requested.
          </p>
        </div>
      )}
    </div>
  );
}
